import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { matchPath, useLocation } from 'react-router-dom'

import Auth from '../../helpers/auth'
import {
  freshchatClearPersistedState,
  freshchatDestroy,
  freshchatGetUser,
  freshchatPrimeExternalId,
  freshchatResetSession,
  freshchatSetContextProperties,
  freshchatSetConversationProperties,
  freshchatSetIdentity,
} from './freshchatClient'
import {
  getCachedMatchLabel,
  getLastMatchContext,
  getOrCreateAnonId,
  setCachedMatchLabel,
  setLastMatchContext,
} from './freshchatStorage'

type FreshchatContextValue = {
  setMatchLabel: (label: string | null) => void
}

const FreshchatContext = createContext<FreshchatContextValue | null>(null)

export const useFreshchat = () => {
  return useContext(FreshchatContext)
}

type Props = {
  authenticated: boolean
  authenticating?: boolean
  children: React.ReactNode
}

const MATCH_ROUTE_PATTERNS = ['/game/:id/:type/*', '/game/:id/:type'] as const

const APP_ID = 'cls-plus'
const SPORT_ID = 'cricket'

const isDevEnv = () => String(import.meta.env.VITE_ENV || '').toUpperCase() === 'DEV'
const isTestHostname = () => {
  if (typeof window === 'undefined') return false
  return window.location.hostname === 'test.clsplus.sportradar.cricket'
}

const isDiagnosticsEnabled = () => {
  const forced = String(import.meta.env.VITE_FRESHCHAT_DIAGNOSTICS_ENABLED || '').toLowerCase() === 'true'
  return forced || isDevEnv() || isTestHostname()
}

const logFreshchatDiagnostics = (event: string, details: Record<string, unknown>) => {
  if (!isDiagnosticsEnabled()) return
  // Avoid logging tokens/PII beyond email/ids we intentionally use for identity.
  // eslint-disable-next-line no-console
  console.groupCollapsed(`[Freshchat][DEV] ${event}`)
  // eslint-disable-next-line no-console
  console.log(details)
  // eslint-disable-next-line no-console
  console.groupEnd()
}

const logFreshchatUserGet = async (context: {
  externalId: string
  userType: 'logged-in' | 'anonymous'
  route: string
}) => {
  if (!isDiagnosticsEnabled()) return
  const result = await freshchatGetUser()
  logFreshchatDiagnostics('user:get', {
    ...context,
    result,
  })
}

const sanitizeExternalIdSegment = (value: string) => {
  // Keep mostly human-readable, but stable and delimiter-safe.
  // Freshchat externalId is treated as an opaque string; we avoid pipes and whitespace.
  return value
    .trim()
    .replace(/\|/g, '_')
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9@._:-]/g, '_')
}

const shortUserLabel = (value: string) => {
  if (!value) return 'unknown'
  if (value.includes('@')) return value.split('@')[0] || value
  if (value.startsWith('anon_')) return value.slice(-8)
  return value.slice(0, 18)
}

const deriveEffectiveMode = (pathname: string, routeMode: string | undefined) => {
  if (pathname.includes('/post-match')) return 'postMatch'
  if (pathname.includes('/scorebook/manual')) return 'postMatch'
  return routeMode
}

const parseMatchRouteContext = (pathname: string) => {
  const match =
    matchPath({ path: MATCH_ROUTE_PATTERNS[0], end: false }, pathname) ||
    matchPath({ path: MATCH_ROUTE_PATTERNS[1], end: false }, pathname)

  let matchId = match?.params?.id || null
  let routeMode = match?.params?.type

  // Fallback: in some builds/routes, matchPath can fail to match nested paths.
  // Parse `/game/:id/:type/...` manually.
  if (!matchId) {
    const parts = pathname.split('/').filter(Boolean)
    const gameIndex = parts.indexOf('game')
    if (gameIndex >= 0) {
      const candidateMatchId = parts[gameIndex + 1]
      const candidateType = parts[gameIndex + 2]
      if (candidateMatchId && candidateType) {
        matchId = candidateMatchId
        routeMode = candidateType
      }
    }
  }

  const mode = deriveEffectiveMode(pathname, routeMode)

  return {
    matchId,
    routeMode,
    mode,
  }
}

const computeMatchLabel = (matchId: string, customLabel: string | null) => {
  if (customLabel) return customLabel
  return `Match ${matchId}`
}

const formatModeLabel = (mode: string | undefined) => {
  if (!mode) return null
  switch (mode) {
    case 'core':
      return 'Core'
    case 'advanced':
      return 'Adv.'
    case 'fielding':
      return 'Field.'
    case 'postMatch':
      return 'Post.'
    default:
      return mode
  }
}

const buildFreshchatTitle = (segments: Array<string | null | undefined>) => {
  const filtered = segments.filter((segment): segment is string => Boolean(segment))
  if (!filtered.length) return 'CLS:'
  const [first, ...rest] = filtered
  return `CLS: ${first}${rest.length ? `|${rest.join('|')}` : ''}`
}

export const FreshchatProvider = ({ authenticated, authenticating = false, children }: Props) => {
  const location = useLocation()

  const [matchLabel, setMatchLabelState] = useState<string | null>(null)
  const prevMatchIdRef = useRef<string | null>(null)
  const prevModeRef = useRef<string | undefined>(undefined)
  const identityRetryTimerRef = useRef<number | null>(null)
  const sessionKeyRef = useRef<string | null>(null)
  const prevAuthenticatedRef = useRef<boolean>(authenticated)
  const hasSetIdentityRef = useRef<boolean>(false)

  const currentMatchIdRef = useRef<string | null>(null)
  const matchLabelMatchIdRef = useRef<string | null>(null)

  const setMatchLabel = useCallback((label: string | null) => {
    // In practice, `Game` can compute the label before this provider's route-sync
    // effect has run. Fall back to parsing the current URL so we don't drop the label.
    const derivedMatchId = parseMatchRouteContext(window.location.pathname).matchId
    const matchId = currentMatchIdRef.current || derivedMatchId
    if (matchId && currentMatchIdRef.current !== matchId) {
      currentMatchIdRef.current = matchId
    }
    if (!matchId) {
      // Ignore label updates outside a match context, but allow clearing.
      if (label === null) {
        setMatchLabelState(null)
        matchLabelMatchIdRef.current = null
      }
      return
    }

    matchLabelMatchIdRef.current = matchId
    setMatchLabelState(label)

    if (label) {
      setCachedMatchLabel(matchId, label)
    }
  }, [])

  const contextValue = useMemo<FreshchatContextValue>(() => ({ setMatchLabel }), [setMatchLabel])

  // Identity sync: anonymous until login, then identify via email.
  useEffect(() => {
    // On refresh while logged in, there can be a window where tokens exist but
    // `authenticated` hasn't been resolved yet. Initializing Freshchat as
    // anonymous during that window triggers identity churn and can crash the widget.
    const hasTokens = (() => {
      try {
        return Boolean(Auth.getTokens())
      } catch {
        return false
      }
    })()

    // If tokens exist but auth isn't confirmed, prime the widget's boot externalId
    // (so refresh doesn't boot anonymous and then identity-switch/wipe).
    if (hasTokens && !authenticated) {
      try {
        const { matchId, mode } = parseMatchRouteContext(location.pathname)
        const matchIdKey = matchId || 'no_match'
        const modeKey = mode || 'no_mode'
        const profile = Auth.getUserProfile()
        const email = profile?.email
        if (email) {
          const externalId = [APP_ID, SPORT_ID, matchIdKey, modeKey, email].map(sanitizeExternalIdSegment).join('|')
          freshchatPrimeExternalId(externalId)
        }
      } catch {
        // ignore
      }
      return
    }

    if (authenticating) {
      void (async () => {
        // Only wipe persisted widget state when we truly don't have tokens
        // (e.g. starting an OAuth redirect/login exchange). During a normal
        // token validation refresh, this would incorrectly clear conversations.
        if (hasTokens) return

        try {
          await freshchatDestroy()
        } catch {
          // ignore
        }

        try {
          await freshchatClearPersistedState()
        } catch {
          // ignore
        }

        // Also clear any in-memory match/title context.
        setMatchLabelState(null)
        prevMatchIdRef.current = null
        prevModeRef.current = undefined
        sessionKeyRef.current = null
        currentMatchIdRef.current = null
        matchLabelMatchIdRef.current = null
        hasSetIdentityRef.current = false
      })()
      return
    }

    if (identityRetryTimerRef.current) {
      window.clearTimeout(identityRetryTimerRef.current)
      identityRetryTimerRef.current = null
    }

    let cancelled = false

    const syncIdentity = async () => {
      const { matchId, routeMode, mode } = parseMatchRouteContext(location.pathname)

      const shouldResetSessionForTransition = (prevSessionKey: string | null, nextSessionKey: string) => {
        if (prevSessionKey === null) return false
        if (prevSessionKey === nextSessionKey) return false
        // Leaving a match back to the lobby should wipe match context (fine), but
        // refreshing while already on the lobby should not wipe chat.
        if (nextSessionKey === 'no_match') return prevSessionKey !== 'no_match'
        return true
      }

      currentMatchIdRef.current = matchId
      // If we have a label but lost the association (e.g. after refresh/order-of-effects),
      // re-associate it to the current match. We only do this when association is missing
      // (null) to avoid accidentally carrying a label across match switches.
      if (matchId && matchLabel && matchLabelMatchIdRef.current === null) {
        matchLabelMatchIdRef.current = matchId
      }

      const cachedMatchLabel = matchId ? getCachedMatchLabel(matchId) : null
      const scopedMatchLabel =
        matchId && (matchLabelMatchIdRef.current === matchId || matchLabelMatchIdRef.current === null)
          ? matchLabel
          : null

      const effectiveMatchLabel = scopedMatchLabel || cachedMatchLabel

      // If auth state flips (logout/login), ensure we don't carry over stale
      // user/match titles while profile is still loading.
      if (prevAuthenticatedRef.current !== authenticated) {
        prevAuthenticatedRef.current = authenticated
        sessionKeyRef.current = null
        // Avoid clearing the conversation on initial refresh where `authenticated`
        // flips from false->true after token validation.
        if (hasSetIdentityRef.current) {
          // Anonymous -> logged-in: we want a wipe, but also must remove any
          // persisted anon widget state that can cause the *first* logged-in
          // refresh to trigger an internal resetSession/router crash.
          if (authenticated) {
            try {
              await freshchatDestroy()
            } catch {
              // ignore
            }
            try {
              await freshchatClearPersistedState()
            } catch {
              // ignore
            }
            hasSetIdentityRef.current = false
          } else {
            await freshchatResetSession()
          }
        }
      }

      const matchIdKey = matchId || 'no_match'
      const modeKey = mode || 'no_mode'

      const desiredMatchSessionKey = matchId ? `${matchIdKey}|${modeKey}` : 'no_match'

      if (authenticated) {
        const profile = Auth.getUserProfile()
        const email = profile?.email
        if (email) {
          // Reset session on transitions (not first load). This creates a new
          // Freshchat thread when moving between lobby/match/mode contexts.
          if (sessionKeyRef.current === null) {
            sessionKeyRef.current = desiredMatchSessionKey
          } else if (sessionKeyRef.current !== desiredMatchSessionKey) {
            const prevSessionKey = sessionKeyRef.current
            sessionKeyRef.current = desiredMatchSessionKey
            if (shouldResetSessionForTransition(prevSessionKey, desiredMatchSessionKey)) {
              await freshchatResetSession()
            }
          }

          const userKey = email
          const externalId = [APP_ID, SPORT_ID, matchIdKey, modeKey, userKey].map(sanitizeExternalIdSegment).join('|')

          logFreshchatDiagnostics('identity:computed', {
            authenticated,
            userType: 'logged-in',
            email,
            matchId,
            routeMode,
            effectiveMode: mode,
            matchLabel,
            matchLabelMatchId: matchLabelMatchIdRef.current,
            cachedMatchLabel,
            computedExternalId: externalId,
            route: location.pathname,
            freshchat: {
              enabled: String(import.meta.env.VITE_FRESHCHAT_ENABLED || '').toLowerCase() === 'true',
              host: import.meta.env.VITE_FRESHCHAT_HOST,
              hasToken: Boolean(import.meta.env.VITE_FRESHCHAT_TOKEN),
              conversationPropsEnabled:
                String(import.meta.env.VITE_FRESHCHAT_CONVERSATION_PROPERTIES_ENABLED || '').toLowerCase() === 'true',
              widgetPresent: Boolean(window.fcWidget),
              widgetInitialized: Boolean(window.__clsPlusFreshchatInitialized),
              initExternalId: window.__clsPlusFreshchatInitExternalId,
              initCalled: Boolean(window.__clsPlusFreshchatInitCalled),
              settingsExternalId: window.fcSettings?.externalId,
            },
          })

          const modeLabel = matchId ? formatModeLabel(mode) : null
          const matchTitle = matchId ? computeMatchLabel(matchId, effectiveMatchLabel) : null
          const title = buildFreshchatTitle([modeLabel, matchTitle, email])
          const lastNameRaw = title.replace(/^CLS:\s*/, '')
          // Freshchat sometimes ignores contact name updates when the string contains
          // pipe-heavy or otherwise unusual characters. Use a safer display string.
          const lastName = lastNameRaw.replace(/\|/g, ' - ').slice(0, 120)

          logFreshchatDiagnostics('title:computed', {
            matchId,
            modeLabel,
            matchTitle,
            matchLabel,
            matchLabelMatchId: matchLabelMatchIdRef.current,
            cachedMatchLabel,
            email,
            title,
            firstName: 'CLS:',
            lastName,
            lastNameRaw,
          })

          const didChangeExternalId = await freshchatSetIdentity({
            externalId,
            email,
            userId: profile?.['cognito:username'],
            firstName: 'CLS:',
            lastName,
            isAnonymous: false,
          })

          hasSetIdentityRef.current = true

          // Always push match/mode properties so the label can upgrade from
          // `Match <uuid>` to `TeamA vs TeamB` once match data arrives.
          await freshchatSetContextProperties({
            matchId: matchId || '',
            mode: mode || '',
            match: matchId ? computeMatchLabel(matchId, effectiveMatchLabel) : '',
          })

          if (matchId) {
            await freshchatSetConversationProperties({
              matchId,
              mode: mode || '',
              Match: computeMatchLabel(matchId, effectiveMatchLabel),
              Mode: mode || '',
              clsTitle: title,
            })
          }

          if (didChangeExternalId) {
            await logFreshchatUserGet({
              externalId,
              userType: 'logged-in',
              route: location.pathname,
            })
          }

          return
        }

        // Logged-in flow but profile isn't ready yet. Clear match props eagerly
        // so we don't show stale match info from a previous session.
        await freshchatSetContextProperties({
          matchId: matchId || '',
          mode: mode || '',
          match: matchId ? computeMatchLabel(matchId, effectiveMatchLabel) : '',
        })

        // Authenticated but profile/email not ready yet; retry shortly so we
        // don't get stuck showing the anonymous title.
        if (!cancelled) {
          identityRetryTimerRef.current = window.setTimeout(() => {
            if (!cancelled) syncIdentity()
          }, 400)
        }
        return
      }

      const anonId = getOrCreateAnonId()

      // Reset session on transitions (not first load). This creates a new
      // Freshchat thread when moving between lobby/match/mode contexts.
      if (sessionKeyRef.current === null) {
        sessionKeyRef.current = desiredMatchSessionKey
      } else if (sessionKeyRef.current !== desiredMatchSessionKey) {
        const prevSessionKey = sessionKeyRef.current
        sessionKeyRef.current = desiredMatchSessionKey
        if (shouldResetSessionForTransition(prevSessionKey, desiredMatchSessionKey)) {
          await freshchatResetSession()
        }
      }

      const userKey = anonId
      const externalId = [APP_ID, SPORT_ID, matchIdKey, modeKey, userKey].map(sanitizeExternalIdSegment).join('|')

      logFreshchatDiagnostics('identity:computed', {
        authenticated,
        userType: 'anonymous',
        anonId,
        matchId,
        routeMode,
        effectiveMode: mode,
        matchLabel,
        matchLabelMatchId: matchLabelMatchIdRef.current,
        cachedMatchLabel,
        computedExternalId: externalId,
        route: location.pathname,
        freshchat: {
          enabled: String(import.meta.env.VITE_FRESHCHAT_ENABLED || '').toLowerCase() === 'true',
          host: import.meta.env.VITE_FRESHCHAT_HOST,
          hasToken: Boolean(import.meta.env.VITE_FRESHCHAT_TOKEN),
          conversationPropsEnabled:
            String(import.meta.env.VITE_FRESHCHAT_CONVERSATION_PROPERTIES_ENABLED || '').toLowerCase() === 'true',
          widgetPresent: Boolean(window.fcWidget),
          widgetInitialized: Boolean(window.__clsPlusFreshchatInitialized),
          initExternalId: window.__clsPlusFreshchatInitExternalId,
          initCalled: Boolean(window.__clsPlusFreshchatInitCalled),
          settingsExternalId: window.fcSettings?.externalId,
        },
      })

      const anonLabel = `anon:${shortUserLabel(anonId)}`
      const modeLabel = matchId ? formatModeLabel(mode) : null
      const matchTitle = matchId ? computeMatchLabel(matchId, effectiveMatchLabel) : null
      const title = buildFreshchatTitle([modeLabel, matchTitle, anonLabel])
      const lastNameRaw = title.replace(/^CLS:\s*/, '')
      const lastName = lastNameRaw.replace(/\|/g, ' - ').slice(0, 120)

      logFreshchatDiagnostics('title:computed', {
        matchId,
        modeLabel,
        matchTitle,
        matchLabel,
        matchLabelMatchId: matchLabelMatchIdRef.current,
        cachedMatchLabel,
        anonLabel,
        title,
        firstName: 'CLS:',
        lastName,
        lastNameRaw,
      })

      const didChangeExternalId = await freshchatSetIdentity({
        externalId,
        anonId,
        firstName: 'CLS:',
        lastName,
        isAnonymous: true,
      })

      hasSetIdentityRef.current = true

      // Always push match/mode properties so the label can upgrade from
      // `Match <uuid>` to `TeamA vs TeamB` once match data arrives.
      await freshchatSetContextProperties({
        matchId: matchId || '',
        mode: mode || '',
        match: matchId ? computeMatchLabel(matchId, effectiveMatchLabel) : '',
      })

      if (matchId) {
        await freshchatSetConversationProperties({
          matchId,
          mode: mode || '',
          Match: computeMatchLabel(matchId, effectiveMatchLabel),
          Mode: mode || '',
          clsTitle: title,
        })
      }

      if (didChangeExternalId) {
        await logFreshchatUserGet({
          externalId,
          userType: 'anonymous',
          route: location.pathname,
        })
      }
    }

    syncIdentity()
    return () => {
      cancelled = true
    }
  }, [authenticated, authenticating, location.pathname, matchLabel])

  // Route sync: update matchId/mode/lastMatch*, and match label when available.
  useEffect(() => {
    const hasTokens = (() => {
      try {
        return Boolean(Auth.getTokens())
      } catch {
        return false
      }
    })()
    if (hasTokens && !authenticated) return

    // Avoid bootstrapping Freshchat without a stable logged-in identity.
    // If we call freshchatSetContextProperties() before email is available,
    // Freshchat may initialize with an anonymous externalId and later crash/wipe
    // when identifyUser runs.
    if (authenticated) {
      const email = Auth.getUserProfile()?.email
      if (!email) return
    }

    const { matchId, mode } = parseMatchRouteContext(location.pathname)
    currentMatchIdRef.current = matchId

    const scopedMatchLabel = matchId && matchLabelMatchIdRef.current === matchId ? matchLabel : null

    const prevMatchId = prevMatchIdRef.current

    if (prevMatchId && !matchId) {
      setLastMatchContext(prevMatchId, prevModeRef.current)
      setMatchLabelState(null)
      matchLabelMatchIdRef.current = null
    }

    if (prevMatchId && matchId && prevMatchId !== matchId) {
      // Entering a new match: avoid reusing previous match's label.
      setMatchLabelState(null)
      matchLabelMatchIdRef.current = null
    }

    prevMatchIdRef.current = matchId
    prevModeRef.current = mode

    const last = getLastMatchContext()

    if (matchId) {
      freshchatSetContextProperties({
        matchId,
        mode: mode ?? '',
        match: computeMatchLabel(matchId, scopedMatchLabel),
        lastMatchId: last?.matchId,
        lastMode: last?.mode,
      })
    } else {
      freshchatSetContextProperties({
        matchId: '',
        mode: '',
        match: '',
        lastMatchId: last?.matchId,
        lastMode: last?.mode,
      })
    }
  }, [authenticated, authenticating, location.pathname, matchLabel])

  // Start last-match timer on tab close/reload.
  useEffect(() => {
    const onBeforeUnload = () => {
      const { matchId, mode } = parseMatchRouteContext(window.location.pathname)

      if (matchId) {
        setLastMatchContext(matchId, mode)
      }
    }

    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [])

  return <FreshchatContext.Provider value={contextValue}>{children}</FreshchatContext.Provider>
}

FreshchatProvider.displayName = 'FreshchatProvider'
